import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fieldPermissionService } from '../services/auth/fieldPermissionService';
import { fieldMappingService } from '../config/fieldMapping';
import { getFormStepIdToSections } from '../config/stepSectionMap';
import { AppRole } from '../types/auth';

interface StepVisibility {
  [stepId: string]: boolean;
}

interface UseStepVisibilityResult {
  visibleSteps: string[];
  stepVisibility: StepVisibility;
  isLoading: boolean;
  refreshVisibility: () => Promise<void>;
}

/**
 * Hook to determine which form steps should be visible based on field permissions.
 * A step is visible if at least one field in any of its sections is not hidden.
 *
 * **Basic/advanced:** when `requestStage` is `DRAFT`, non–partner-contributor checks use the
 * Requestor role so basic view matches create-request parity.
 */
export const useStepVisibility = (
  requestStage?: string,
  requestId?: string,
): UseStepVisibilityResult => {
  const { user } = useAuth();
  const [stepVisibility, setStepVisibility] = useState<StepVisibility>({});
  const [isLoading, setIsLoading] = useState(true);

  // Track the last stage and request to distinguish between request switches vs stage changes
  const [lastStage, setLastStage] = useState<string | undefined>(undefined);
  const [lastRequestId, setLastRequestId] = useState<string | undefined>(undefined);
  const computationVersion = useRef(0);

  const checkStepVisibility = useCallback(async () => {
    const version = ++computationVersion.current;
    // Check if this is a request switch vs a stage change
    const isRequestSwitch = requestId !== lastRequestId;
    const isStageChangeWithinSameRequest = !isRequestSwitch && requestStage !== lastStage;

    if (isRequestSwitch) {
      setLastRequestId(requestId);
      setLastStage(requestStage);
      // Don't clear cache on request switch - it's normal to have different stages across requests
    } else if (isStageChangeWithinSameRequest) {
      if (lastStage) {
        fieldPermissionService.clearCache(lastStage);
      }
      setLastStage(requestStage);
    }

    if (!user?.roles) {
      // If no user, hide all steps except summary
      const visibility: StepVisibility = {};
      Object.keys(getFormStepIdToSections()).forEach(stepId => {
        visibility[stepId] = stepId === 'summary';
      });
      setStepVisibility(visibility);
      setIsLoading(false);
      return;
    }

    // If we don't have a stage at all, show default visibility (not loading)
    if (!requestStage) {
      // Show all steps by default when no stage is provided (e.g., during loading)
      const visibility: StepVisibility = {};
      Object.keys(getFormStepIdToSections()).forEach(stepId => {
        visibility[stepId] = true;
      });
      setStepVisibility(visibility);
      setIsLoading(false); // Don't stay in loading state when showing defaults
      return;
    }

    setIsLoading(true);
    const visibility: StepVisibility = {};

    try {
      // BASIC VIEW MODE: When checking DRAFT stage, always use only "Requestor" role
      // This shows exactly what a requestor would see when creating a new request
      // regardless of who is actually viewing the request
      // EXCEPTION: Partner Contributors with request-specific access levels should always
      // use their actual roles, not override to Requestor, because their permissions are
      // based on the access level (read/read_write) assigned to them for specific requests
      const isBasicViewMode = requestStage === 'DRAFT';
      const isPartnerContributor = user.roles.includes(AppRole.PARTNER_CONTRIBUTOR);
      const effectiveRoles = (isBasicViewMode && !isPartnerContributor) ? [AppRole.REQUESTOR] : user.roles;

      // Check each step
      for (const [stepId, sections] of Object.entries(getFormStepIdToSections())) {
        // Summary step is always visible
        if (stepId === 'summary') {
          visibility[stepId] = true;
          continue;
        }

        // If no sections defined, assume visible (shouldn't happen)
        if (sections.length === 0) {
          visibility[stepId] = true;
          continue;
        }

        let hasVisibleField = false;

        // Check all sections for this step
        for (const section of sections) {
          // Get all fields for this section from the field mapping
          const mappings = fieldMappingService.getMappingsForJsonSection(section);

          if (mappings.length === 0) {
            // If no mappings found, assume section is visible
            hasVisibleField = true;
            break;
          }

          // Check each field in the section
          for (const mapping of mappings) {
            try {
              // Skip global utility fields that don't represent actual step content
              const isGlobalField = mapping.jsonField === 'Select UOM';

              // Check if field is visible for the effective roles in the current stage
              const fieldAccess = await fieldPermissionService.checkFieldAccess({
                userRoles: effectiveRoles,
                section: mapping.jsonSection,
                field: mapping.jsonField,
                requestStage: requestStage,
                requestId: requestId,
                userEmail: user.email,
              });

              if (!fieldAccess?.isHidden && !isGlobalField) {
                hasVisibleField = true;
                break;
              }
            } catch (error) {
              console.error(`Error checking field access for ${mapping.jsonSection}.${mapping.jsonField}:`, error);
              // On error, assume field is visible (fail-safe)
              hasVisibleField = true;
              break;
            }
          }

          // If we found a visible field, no need to check other sections
          if (hasVisibleField) {
            break;
          }
        }

        visibility[stepId] = hasVisibleField;
        // console.log(`🎯 Final visibility for step ${stepId}: ${hasVisibleField}`);
      }

      if (version !== computationVersion.current) return;
      // console.log('🎯 Final step visibility summary:', visibility);
      setStepVisibility(visibility);
    } catch (error) {
      console.error('Error checking step visibility:', error);
      if (version !== computationVersion.current) return;
      // On error, show all steps (fail-safe)
      Object.keys(getFormStepIdToSections()).forEach(stepId => {
        visibility[stepId] = true;
      });
      setStepVisibility(visibility);
    } finally {
      if (version === computationVersion.current) {
        setIsLoading(false);
      }
    }
  }, [user?.roles, user?.email, requestStage, requestId, lastStage, lastRequestId]);

  useEffect(() => {
    checkStepVisibility();
  }, [checkStepVisibility]);

  // Get list of visible step IDs
  const visibleSteps = Object.keys(stepVisibility).filter(stepId => stepVisibility[stepId]);

  return {
    visibleSteps,
    stepVisibility,
    isLoading,
    refreshVisibility: checkStepVisibility,
  };
};

/**
 * Hook to check if a specific step is visible
 */
export const useIsStepVisible = (stepId: string, requestStage?: string, requestId?: string): boolean => {
  const { stepVisibility } = useStepVisibility(requestStage, requestId);
  return stepVisibility[stepId] ?? true; // Default to visible if not determined yet
};